import React, { useEffect, useState } from 'react';
import { useLanguageLocale } from '../../../hooks/language-locale-hooks';
import { Paper, Typography, TextField, Button, CircularProgress, Box } from '@mui/material';
import { ContentContainer } from '../../../components/ContentContainer/ContentContainer';
import { useSelectedOrganizationData, isRecordFound, useUserRoles } from '../../../hooks/data-hooks';
import { isDataLoaded, RoleLocationList, SiteInfo, UserRole } from '../../../types';
import { EMAIL_REGEX } from '../../../constants';
import { ConfirmationPopUp } from '../../../components/ConfirmationPopUp/ConfirmationPopUp';
import { useHistory } from 'react-router-dom';
import { pushAppRoute } from '../../../data/domain/route-manager';
import { useRegistrationRolesData } from '../../../data/DataProviders/RegistrationRoleProvider';
import { useDispatch, useSelector } from 'react-redux';
import * as PXBColors from '@brightlayer-ui/colors';
import { Selectors } from '../../../selectors';
import userStyles from '../UserStyle';
import { Thunks } from '../../../actions/thunks';
import { Actions } from '../../../actions';
import { useToasts } from '../../../data/DataProviders/ToastProvider';
import { DataIndicators } from '../../../components/DataIndicators';
import { RoleLocation } from '../../../components/RoleLocation/RoleLocation';
import { HeaderInfo } from '../../../components/HeaderInfo/HeaderInfo';
import { NavigationPromptModal } from '../../../components/NavigationPromptModal/NavigationPromptModal';

export const AddUser = (): JSX.Element => {
    const classes = userStyles();
    const { t } = useLanguageLocale();
    const history = useHistory();
    const dispatch = useDispatch();
    const [email, setEmail] = useState('');
    const selectedOrganization = useSelectedOrganizationData();
    const registrationRole = useRegistrationRolesData();
    const registrationState = useSelector(Selectors.Registration.registration);
    const { addToast } = useToasts();
    const orgId = isRecordFound(selectedOrganization) ? selectedOrganization.value.id : '';
    const orgName = isRecordFound(selectedOrganization) ? selectedOrganization.value.name : '';
    const [alerted, setAlerted] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [invalidEmailMessage, setInvalidEmailMessage] = useState(false);
    const [disableLocation, setDisableLocation] = useState<SiteInfo[]>([]);
    const userRole = useUserRoles();
    const [roleLocationList, setRoleLocationList] = useState<RoleLocationList>([
        {
            index: 0,
            roleId: '0',
            disabledLocations: [],
            roleList: [],
            selectedLocation: [],
        },
    ]);

    useEffect(() => {
        const hasDirty = email !== '' || roleLocationList.every((role) => role.roleId !== '0');
        setIsDirty(hasDirty);
    }, [email, roleLocationList]);

    useEffect(() => {
        if (isDataLoaded(registrationRole) && isRecordFound(userRole)) {
            /** update rolelist on load of the page in the list */
            setRoleLocationList([
                {
                    index: 0,
                    roleId: '',
                    disabledLocations: [],
                    roleList: registrationRole.value.filter((x) => x.roleName !== UserRole.Unassigned),
                    selectedLocation: [],
                },
            ]);
            const siteUser = userRole.value.filter((x) => x.roleName === UserRole.SiteUser);
            const siteUserData: SiteInfo[] = siteUser.map((site) => ({
                siteId: site.siteId,
                siteName: site.siteName,
            }));
            setDisableLocation(siteUserData);
        }
    }, [registrationRole]); // eslint-disable-line react-hooks/exhaustive-deps
    /** show popup for if user is already registrated */
    useEffect(() => {
        if (registrationState.errorCode !== '') {
            const alert = registrationState.errorCode.split('/');
            if (alert.length > 0 && Number(alert[0]) === 3007) {
                setAlerted(true);
                setAlertMessage(alert[1].toString());
            } else if (alert.length > 0 && Number(alert[0]) === 9007) {
                setInvalidEmailMessage(true);
            }
        }
    }, [registrationState.errorCode]);
    /** remove the popup on page unload */
    useEffect(() => {
        dispatch(Actions.Registration.inviteUserUnmounted());
        setAlerted(false);
    }, []); //eslint-disable-line react-hooks/exhaustive-deps

    const validEmail = (emailId: string): boolean =>
        Boolean(
            emailId &&
            typeof emailId === 'string' &&
            emailId.length > 0 &&
            new RegExp(EMAIL_REGEX).test(emailId) &&
            emailId.split('@')[0].length <= 64 &&
            emailId.split('@')[1].length <= 48
        );
    /** Invite button should be visible on only when all fields have correct value */
    const canInvite = (roleList: Array<{ roleId: string; roleName: string }>): boolean => {
        const validRoleId = roleLocationList.every((x) => x.roleId !== '');
        const selectedRoleId = roleLocationList.length === 1 && roleLocationList[0].roleId;
        const selectedRoleName = selectedRoleId && roleList.find((role) => role.roleId === selectedRoleId);

        const validLocation =
            selectedRoleName && selectedRoleName.roleName === UserRole.EatonAdmin
                ? true
                : roleLocationList.every(
                    (y) => y.selectedLocation.length > 0 && y.selectedLocation.every((z) => z.siteId !== '')
                );

        const result = validEmail(email) && validRoleId && validLocation;
        return result;
    };
    /** api call for invite user */
    const userInvite = (): void => {
        setInvalidEmailMessage(false);
        const result = roleLocationList.map((roleLoc) => ({
            roleId: roleLoc.roleId,
            siteIds: roleLoc.selectedLocation.map((x) => x.siteId),
        }));
        dispatch(Thunks.Registration.inviteUserRegistration(email, result, addToast));
    };
    const handleClose = (): void => {
        dispatch(Actions.Registration.inviteUserUnmounted());
        pushAppRoute(history, { type: 'ManageUser', orgId: orgId });
    };
    const handleCancel = (): void => {
        dispatch(Actions.Registration.inviteUserUnmounted());
        setAlerted(false);
    };

    const addRoleChange = (data: RoleLocationList): void => {
        setRoleLocationList(data);
    };
    const removeAdditionalRow = (data: RoleLocationList): void => {
        setRoleLocationList(data);
    };
    const onSelectedLocation = (data: RoleLocationList): void => {
        setRoleLocationList(data);
    };
    const onAddRow = (data: RoleLocationList): void => {
        setRoleLocationList(data);
    };
    return (
        <>
            <DataIndicators
                errorMessage="No Data"
                emptyMessage="No Data"
                data={registrationRole}
                emptyIcon="Person"
                isEmpty={false}
            />

            {isDataLoaded(registrationRole) && (
                <>
                    <ContentContainer>
                        <div className={classes.formDiv}>
                            <Paper style={{ padding: 48 }}>
                                <HeaderInfo title="Add User to" entity="to add a User" value={orgName} />

                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="left"

                                >
                                    <Box className={`${classes.paddingTextbox}`}>


                                        <TextField
                                            inputProps={{
                                                'data-testid': 'email',
                                            }}
                                            fullWidth
                                            variant="filled"
                                            required
                                            value={email}
                                            onChange={(e): void => setEmail(e.target.value)}
                                            id="email"
                                            label="Email Address"
                                            error={invalidEmailMessage}
                                            helperText={invalidEmailMessage ? 'Incorrect Email Id' : ''}
                                        />
                                    </Box>



                                    <Box className={`${classes.padding8} ${classes.paddingTop0} ${classes.paddingBottom24}`}>
                                        <RoleLocation
                                            roleData={registrationRole.value}
                                            onRoleChange={addRoleChange}
                                            removeAddRow={removeAdditionalRow}
                                            onSelectLocation={onSelectedLocation}
                                            existingLocation={disableLocation}
                                            isEdit={true}
                                            AddRow={onAddRow}
                                        ></RoleLocation>
                                    </Box>
                                    <Box className={classes.inviteUserButton}>
                                        <Button
                                            style={{ marginRight: 15 }}
                                            variant="outlined"
                                            color="primary"
                                            data-testid="cancel-user"
                                            onClick={(): void => {
                                                pushAppRoute(history, {
                                                    type: 'ManageUser',
                                                    orgId: orgId,
                                                });
                                            }}
                                        >
                                            {t('ACTIONS.CANCEL')}
                                        </Button>
                                        {registrationState.inviteUserLoading && (
                                            <Button
                                                variant="contained"
                                                disabled
                                                startIcon={<CircularProgress size="1rem" style={{ color: 'white' }} />}
                                                style={{
                                                    height: '40px',
                                                    marginRight: 15,
                                                    backgroundColor: PXBColors.blue[200],
                                                    color: 'white',
                                                }}
                                            >
                                                {t('ADDUSER.INVITEUSER')}
                                            </Button>
                                        )}
                                        {!registrationState.inviteUserLoading && (
                                            <Button
                                                disabled={!canInvite(registrationRole.value)}
                                                variant="contained"
                                                color="primary"
                                                data-testid="invite-user"
                                                onClick={userInvite}
                                            >
                                                {t('ADDUSER.INVITEUSER')}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Paper>
                        </div>
                    </ContentContainer>
                    {registrationState.inviteUserSuccess && (
                        <ConfirmationPopUp
                            title={t('ADDUSER.INVITESUCCESS')}
                            titleColor="primary"
                            body={
                                <>
                                    <Typography variant="subtitle1" style={{ marginBottom: 20 }}>
                                        {t('ADDUSER.ACCOUNTCREATED')}
                                    </Typography>
                                    <Typography variant="subtitle1">{t('ADDUSER.EMAILRECEIVE')}</Typography>
                                </>
                            }
                            okButtonText={t('ACTIONS.OKAY')}
                            show={registrationState.inviteUserSuccess}
                            handleOk={handleClose}
                            okButtonShow={true}
                            width="xs"
                        ></ConfirmationPopUp>
                    )}
                    {alerted && (
                        <ConfirmationPopUp
                            title="Invitation Error"
                            titleColor="error"
                            body={
                                <Typography variant="subtitle1" style={{ marginBottom: 20 }}>
                                    {alertMessage}
                                </Typography>
                            }
                            okButtonText={t('ACTIONS.OKAY')}
                            show={alerted}
                            handleOk={handleCancel}
                            okButtonShow={true}
                            width="xs"
                        ></ConfirmationPopUp>
                    )}
                </>
            )}
            {!registrationState.inviteUserSuccess && <NavigationPromptModal isDirty={isDirty} />}
        </>
    );
};
